/**
 * WelcomeScreen
 *
 * First screen in the learner conversation flow.
 * Shows assessment scenario context, duration estimate,
 * a reassuring frame, and a single "Begin" button.
 */

import type { Assessment } from '../../lib/types';

interface WelcomeScreenProps {
  assessment: Assessment;
  onBegin: () => void;
}

export function WelcomeScreen({ assessment, onBegin }: WelcomeScreenProps) {
  const metadata = assessment.metadata ?? {};
  const title = typeof metadata.title === 'string'
    ? metadata.title
    : 'Voice Assessment';
  const description = typeof metadata.scenarioDescription === 'string'
    ? metadata.scenarioDescription
    : typeof metadata.description === 'string'
      ? metadata.description
      : null;
  const durationMinutes = typeof metadata.estimatedDurationMinutes === 'number'
    ? metadata.estimatedDurationMinutes
    : typeof metadata.warmupDurationMs === 'number'
      ? Math.max(5, Math.ceil(metadata.warmupDurationMs as number / 60000) + 3)
      : 5;

  return (
    <div className="learner-welcome">
      <div className="learner-welcome-card">
        <div className="learner-welcome-icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="24" fill="var(--sky-100)" />
            <path
              d="M24 14C18.48 14 14 18.48 14 24C14 29.52 18.48 34 24 34C29.52 34 34 29.52 34 24C34 18.48 29.52 14 24 14ZM24 30C23.45 30 23 29.55 23 29V23C23 22.45 23.45 22 24 22C24.55 22 25 22.45 25 23V29C25 29.55 24.55 30 24 30ZM25 20H23V18H25V20Z"
              fill="var(--sky-500)"
            />
          </svg>
        </div>

        <h2 className="learner-welcome-title">{title}</h2>

        {description && (
          <p className="learner-welcome-description">{description}</p>
        )}

        <div className="learner-welcome-details">
          <div className="learner-welcome-detail">
            <span className="learner-welcome-detail-label">Estimated Duration</span>
            <span className="learner-welcome-detail-value">~{durationMinutes} minutes</span>
          </div>
          <div className="learner-welcome-detail">
            <span className="learner-welcome-detail-label">Format</span>
            <span className="learner-welcome-detail-value">AI Voice Conversation</span>
          </div>
        </div>

        <div className="learner-welcome-reassurance">
          <p>You will have a voice conversation with an AI assistant who will guide you through the assessment. Speak naturally and take your time.</p>
        </div>

        <button
          className="btn btn-primary learner-welcome-begin"
          onClick={onBegin}
        >
          Begin Assessment
        </button>
      </div>
    </div>
  );
}
